"use client";
import { IBlocks, IUserData } from "@/types";
import React from "react";
import Link from "next/link";
import AdminPopover from "./utils/AdminPopover";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import RenderIconOrAsset from "./utils/RenderIconOrAsset";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import Blacklist from "@/lib/Blacklist";
import {
    BORDER_RADIUS_OPTIONS,
    FONT_SIZE_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";

interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}
export default function BlockMultiLink({
    edit,
    account,
    block,
    update,
    style,
    theme,
}: Props) {
    const BlockClickHandler = (
        button: React.MouseEvent,
        block: IBlocks,
        icon_id: string
    ) => {
        if ((button.button == 0 && !edit) || (button.button == 1 && !edit)) {
            fetch("/api/user/click", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: account.id,
                    block_id: block.id,
                    child_block_id: icon_id,
                }),
            });
        }
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    let backgroundStyle = {};
    if (blockUpdate.colour.toggle && blockUpdate.colour.background_block) {
        backgroundStyle = {
            background: blockUpdate.colour.background_block,
        };
    }
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Multi Link"}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 rounded-user p-2 ${blockUpdate.colour.position == "center" ? "justify-center" : blockUpdate.colour.position == "right" ? "justify-end" : "justify-start"}`}
                style={backgroundStyle}
                id="multiLinkFlex"
            >
                {/* MAP OF ICONS */}
                {blockUpdate.options?.icons?.map((icon: any) => {
                    let new_style = style;
                    if (icon.style.enabled) {
                        new_style = {
                            background: icon.style.background,
                            color: icon.style.text,
                            borderColor: icon.style.border,
                            borderWidth: "2px",
                        };
                    }
                    const contentWarning = Blacklist?.find((e) =>
                        icon.url?.includes(e.find)
                    );
                    if (icon.adult || contentWarning) {
                        return (
                            <Dialog key={icon.id}>
                                <DialogTrigger asChild>
                                    <button
                                        style={new_style}
                                        id="multiLinkButton"
                                        className={cn(
                                            "rounded-user bg-user-primary-color text-user-primary-text-color",
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                                BORDER_RADIUS_OPTIONS[
                                                    blockUpdate.style
                                                        .corner_radius
                                                ]?.class,
                                            blockUpdate.style?.custom &&
                                                SHADOW_OPTIONS[
                                                    blockUpdate.style.shadow
                                                ]?.class,
                                            FONT_SIZE_OPTIONS[
                                                theme.profile.design.font_size
                                            ]?.class,
                                            "flex p-2 text-xl transition-all duration-150 hover:scale-110",
                                            blockUpdate.name.replaceAll(
                                                " ",
                                                "-"
                                            )
                                        )}
                                    >
                                        <RenderIconOrAsset
                                            account_id={account.id}
                                            iconName={icon.icon}
                                            size={
                                                FONT_SIZE_OPTIONS[
                                                    theme.profile.design
                                                        .font_size
                                                ]?.icon || 26
                                            }
                                            raw
                                        />
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="sm:h-fit">
                                    <DialogHeader>
                                        <DialogTitle>
                                            NSFW or mature content ahead
                                        </DialogTitle>
                                        <DialogDescription>
                                            Where you're about to go has been
                                            flagged as mature or NSFW
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="flex flex-col gap-4">
                                        <p className="w-full rounded-md bg-accent p-1 text-center text-sm text-foreground/80">
                                            {icon.url.replace("https://", "")}
                                        </p>
                                        <Separator />
                                        <Label variant={"toggle"}>
                                            Would you like to continue?
                                        </Label>
                                        <div className="flex w-full items-center gap-2">
                                            <Link
                                                href={icon.url}
                                                target="_blank"
                                                prefetch={false}
                                                onClick={(e) => {
                                                    BlockClickHandler(
                                                        e,
                                                        block,
                                                        icon.id
                                                    );
                                                }}
                                                onAuxClick={(e) => {
                                                    BlockClickHandler(
                                                        e,
                                                        block,
                                                        icon.id
                                                    );
                                                }}
                                            >
                                                <Button>Proceed</Button>
                                            </Link>
                                            <DialogClose asChild>
                                                <Button variant={"outline"}>
                                                    Go back
                                                </Button>
                                            </DialogClose>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        );
                    }
                    return (
                        <Link
                            href={icon.url}
                            target="_blank"
                            prefetch={false}
                            key={icon.id}
                            onClick={(e) => {
                                BlockClickHandler(e, block, icon.id);
                            }}
                            onAuxClick={(e) => {
                                BlockClickHandler(e, block, icon.id);
                            }}
                        >
                            <button
                                style={new_style}
                                id="multiLinkButton"
                                className={cn(
                                    "rounded-user bg-user-primary-color text-user-primary-text-color",
                                    SHADOW_OPTIONS[theme.profile.design.shadow]
                                        ?.class,
                                    blockUpdate.style?.custom &&
                                        BORDER_RADIUS_OPTIONS[
                                            blockUpdate.style.corner_radius
                                        ]?.class,
                                    blockUpdate.style?.custom &&
                                        SHADOW_OPTIONS[blockUpdate.style.shadow]
                                            ?.class,
                                    FONT_SIZE_OPTIONS[
                                        theme.profile.design.font_size
                                    ]?.class,
                                    "flex p-2 text-xl transition-all duration-150 hover:scale-110",
                                    blockUpdate.name.replaceAll(" ", "-")
                                )}
                            >
                                <RenderIconOrAsset
                                    account_id={account.id}
                                    iconName={icon.icon}
                                    size={
                                        FONT_SIZE_OPTIONS[
                                            theme.profile.design.font_size
                                        ]?.icon || 26
                                    }
                                    raw
                                />
                            </button>
                        </Link>
                    );
                })}
            </div>
        </AdminPopover>
    );
}
